import * as THREE from 'three';
import { PhysicsWorld } from './physics/PhysicsWorld';
import { Renderer } from './rendering/Renderer';
import { Environment } from './environment/Environment';
import { OrganismManager } from './organisms/OrganismManager';
import { UIManager } from './ui/UIManager';
import { InputHandler } from './input/InputHandler';
import { CameraController } from './camera/CameraController';
import { CameraInputHandler } from './camera/CameraInputHandler';

export type SimulationMode = 'game' | 'scientific';

export class TidePoolSimulation {
  private physicsWorld: PhysicsWorld;
  private renderer: Renderer;
  private environment: Environment;
  private organismManager: OrganismManager;
  private uiManager: UIManager;
  private inputHandler: InputHandler;
  private cameraController: CameraController | null = null;
  private cameraInputHandler: CameraInputHandler | null = null;

  private mode: SimulationMode = 'game';
  private timeScale: number = 1.0; // 1 minute = 1 day in game mode
  private isPaused: boolean = false;
  private simulationTime: number = 0; // in days
  private lastFrameTime: number = 0;

  private animationFrameId: number | null = null;

  // Selection system
  private raycaster: THREE.Raycaster;
  private mouse: THREE.Vector2;
  private selectedOrganism: any | null = null;

  // Telemetry tracking
  private frameCount: number = 0;
  private fpsUpdateTime: number = 0;
  private currentFPS: number = 0;

  constructor() {
    this.physicsWorld = new PhysicsWorld();
    this.renderer = new Renderer();
    this.environment = new Environment(this.mode);
    this.organismManager = new OrganismManager(this.physicsWorld, this.environment);
    this.uiManager = new UIManager();
    this.inputHandler = new InputHandler();

    // Initialize selection system
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
  }

  async initialize() {
    // Initialize renderer
    await this.renderer.initialize();

    // Initialize physics world
    this.physicsWorld.initialize();

    // Initialize camera controller
    this.cameraController = new CameraController(this.renderer.camera);
    const canvas = this.renderer.renderer.domElement;
    this.cameraInputHandler = new CameraInputHandler(this.cameraController, canvas);

    // Set up camera UI controls
    this.setupCameraControls();

    // Set up matrix visualization toggle
    this.setupMatrixVisualizationToggle();

    // Set up creature selection
    this.setupCreatureSelection();

    // Create initial environment
    this.environment.createTidePool(this.physicsWorld, this.renderer.scene);

    // Give organism manager a scene reference for reproduction
    this.organismManager.setScene(this.renderer.scene);

    // Add initial organisms
    this.spawnInitialEcosystem();

    console.log('✓ Simulation initialized');
    this.debugLog('Simulation initialized successfully', '#0f0');
    this.debugLog(`Camera at: (${this.renderer.camera.position.x.toFixed(1)}, ${this.renderer.camera.position.y.toFixed(1)}, ${this.renderer.camera.position.z.toFixed(1)})`, '#0ff');
  }

  private spawnInitialEcosystem() {
    // Spawn organisms in the intertidal/beach zone
    // World coords: X: -4000 to +4000, Z: shoreline at 0, beach 0-100, ocean negative
    // Focus spawning in a visible area near origin but at proper beach coordinates

    const beachAreaX = 100; // Spawn within +/- 100m of center on X axis
    const beachZoneZ = { min: 5, max: 50 }; // Beach zone 5-50m from shoreline

    // Phase 1 MVP organisms
    // Add some algae (producers) - along the shoreline
    for (let i = 0; i < 10; i++) {
      this.organismManager.spawn('sea_lettuce', {
        x: (Math.random() - 0.5) * beachAreaX * 2,
        y: 0.1,
        z: Math.random() * 10 - 5, // Near shoreline, some in water
      }, this.renderer.scene);
    }

    // Add barnacles (filter feeders) - on rocks in tidal zone
    for (let i = 0; i < 15; i++) {
      this.organismManager.spawn('acorn_barnacle', {
        x: (Math.random() - 0.5) * beachAreaX * 2,
        y: 0.1,
        z: Math.random() * 20, // Tidal zone
      }, this.renderer.scene);
    }

    // Add limpets (herbivores) - on rocks
    for (let i = 0; i < 8; i++) {
      this.organismManager.spawn('limpet', {
        x: (Math.random() - 0.5) * beachAreaX * 2,
        y: 0.2,
        z: Math.random() * 30, // Spread across tidal zone
      }, this.renderer.scene);
    }

    // Add sea stars (predators) - in tide pools
    for (let i = 0; i < 3; i++) {
      this.organismManager.spawn('ochre_sea_star', {
        x: (Math.random() - 0.5) * beachAreaX,
        y: 0.3,
        z: Math.random() * 25, // Near water
      }, this.renderer.scene);
    }

    // Add hermit crabs - Star couple: Sheldon & Shelley! 🦀💕🦀
    // Place them on the sandy beach where they belong!

    // Sheldon (male) - the adventurous one, exploring the beach
    const sheldon = this.organismManager.spawn('pacific_hermit_crab', {
      x: -30, // 30m west of center
      y: 0.5, // Slightly elevated for visibility
      z: 25, // On the beach, 25m inland from shore
    }, this.renderer.scene, 'male');

    // Shelley (female) - nearby, searching for shells
    const shelley = this.organismManager.spawn('pacific_hermit_crab', {
      x: -25, // Close to Sheldon
      y: 0.5,
      z: 28, // Slightly more inland
    }, this.renderer.scene, 'female');

    console.log(`✓ Spawned Sheldon at (-30, 0.5, 25):`, sheldon);
    console.log(`✓ Spawned Shelley at (-25, 0.5, 28):`, shelley);
    this.debugLog('Sheldon spawned at (-30, 0.5, 25)', '#ff69b4');
    this.debugLog('Shelley spawned at (-25, 0.5, 28)', '#ff1493');

    // Add a few more hermit crabs (random genders) around the beach
    for (let i = 0; i < 3; i++) {
      this.organismManager.spawn('pacific_hermit_crab', {
        x: (Math.random() - 0.5) * beachAreaX,
        y: 0.5,
        z: beachZoneZ.min + Math.random() * (beachZoneZ.max - beachZoneZ.min),
      }, this.renderer.scene);
    }

    // Add Sea Anemone Home (player's home base) - in a tide pool
    this.organismManager.spawn('sea_anemone_home', {
      x: 0,
      y: 0.1,
      z: 10, // Tide pool area
    }, this.renderer.scene);

    // Add Player-Controlled Clownfish (our hero!) - swimming in tide pool
    this.organismManager.spawn('clownfish', {
      x: 2,
      y: 1.5, // Swimming height
      z: 10, // Near the anemone
    }, this.renderer.scene);

    console.log(`✓ Spawned initial ecosystem with Sheldon & Shelley on the beach!`);
    this.debugLog(`Total organisms spawned: ${this.organismManager.getAllOrganisms().length}`, '#0f0');
  }

  start() {
    this.lastFrameTime = performance.now();
    this.animate();
  }

  private animate = () => {
    this.animationFrameId = requestAnimationFrame(this.animate);

    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastFrameTime) / 1000; // seconds
    this.lastFrameTime = currentTime;

    if (!this.isPaused) {
      this.update(deltaTime);
    }

    this.render();
  };

  private update(deltaTime: number) {
    // Convert real time to simulation time
    const simDeltaTime = this.mode === 'game'
      ? deltaTime * this.timeScale * 1440 // 1 minute = 1 day (1440 minutes/day)
      : deltaTime * this.timeScale; // Real-time for scientific mode

    this.simulationTime += simDeltaTime / 86400; // Convert seconds to days

    // Update environment (tide, temperature, etc.)
    // Pass real deltaTime for wave animation, simDeltaTime for other environmental changes
    this.environment.update(this.simulationTime, simDeltaTime, deltaTime, this.renderer.camera);

    // Update physics
    this.physicsWorld.step(deltaTime);

    // Update organisms (behavior, metabolism, reproduction)
    this.organismManager.update(simDeltaTime, this.simulationTime);

    // Handle player input for clownfish
    const playerFish = this.organismManager.getPlayerClownfish();
    if (playerFish) {
      const moveDir = this.inputHandler.getMovementDirection();
      playerFish.setMoveDirection(moveDir.x, moveDir.z);
    }

    // Update camera
    if (this.cameraController && this.cameraInputHandler) {
      this.cameraInputHandler.update(deltaTime);
      this.cameraController.update(deltaTime);
    }

    // Update UI
    this.updateUI();

    // Update creature profile if selected
    if (this.selectedOrganism) {
      this.updateCreatureProfile();
    }

    // Update telemetry display
    this.updateTelemetry(deltaTime);
  }

  private render() {
    // Update visual representations to match physics
    this.organismManager.syncVisuals();

    // Render the scene
    this.renderer.render();
  }

  private updateUI() {
    const stats = this.organismManager.getStatistics();

    this.uiManager.update({
      time: this.simulationTime,
      tideLevel: this.environment.getTideLevel(),
      waterHeight: this.environment.getWaterLevel(),
      moonPhase: this.environment.getMoonPhaseName(),
      shoreMoisture: this.environment.getShoreMoisture(),
      temperature: this.environment.getTemperature(),
      speciesCount: stats.speciesCount,
      totalOrganisms: stats.totalOrganisms,
      biodiversity: stats.shannonIndex,
    });
  }

  private setupCameraControls() {
    if (!this.cameraController) return;

    // Zoom controls
    const zoomIn = document.getElementById('zoom-in');
    const zoomOut = document.getElementById('zoom-out');
    const zoomSlider = document.getElementById('zoom-slider') as HTMLInputElement;

    if (zoomIn) {
      zoomIn.addEventListener('click', () => this.cameraController!.zoom(-200));
    }
    if (zoomOut) {
      zoomOut.addEventListener('click', () => this.cameraController!.zoom(200));
    }
    if (zoomSlider) {
      zoomSlider.addEventListener('input', (e) => {
        const value = parseFloat((e.target as HTMLInputElement).value);
        this.cameraController!.setZoom(value);
      });
    }

    // Pan controls
    const panUp = document.getElementById('pan-up');
    const panDown = document.getElementById('pan-down');
    const panLeft = document.getElementById('pan-left');
    const panRight = document.getElementById('pan-right');
    const panReset = document.getElementById('pan-reset');

    if (panUp) panUp.addEventListener('click', () => this.cameraController!.pan(0, 3));
    if (panDown) panDown.addEventListener('click', () => this.cameraController!.pan(0, -3));
    if (panLeft) panLeft.addEventListener('click', () => this.cameraController!.pan(-3, 0));
    if (panRight) panRight.addEventListener('click', () => this.cameraController!.pan(3, 0));
    if (panReset) panReset.addEventListener('click', () => {
      this.cameraController!.setTarget(new THREE.Vector3(0, 2, 0));
    });

    // Rotate controls
    const rotateUp = document.getElementById('rotate-up');
    const rotateDown = document.getElementById('rotate-down');
    const rotateLeft = document.getElementById('rotate-left');
    const rotateRight = document.getElementById('rotate-right');
    const rotateReset = document.getElementById('rotate-reset');

    if (rotateUp) rotateUp.addEventListener('click', () => this.cameraController!.orbit(0, 10));
    if (rotateDown) rotateDown.addEventListener('click', () => this.cameraController!.orbit(0, -10));
    if (rotateLeft) rotateLeft.addEventListener('click', () => this.cameraController!.orbit(-10, 0));
    if (rotateRight) rotateRight.addEventListener('click', () => this.cameraController!.orbit(10, 0));
    if (rotateReset) rotateReset.addEventListener('click', () => {
      this.cameraController!.applyPreset('default');
    });

    // Preset controls
    const presetDefault = document.getElementById('preset-default');
    const presetTop = document.getElementById('preset-top');
    const presetSide = document.getElementById('preset-side');
    const presetFront = document.getElementById('preset-front');
    const presetIsometric = document.getElementById('preset-isometric');
    const presetFollow = document.getElementById('preset-follow');

    if (presetDefault) {
      presetDefault.addEventListener('click', () => {
        this.cameraController!.applyPreset('default');
      });
    }
    if (presetTop) {
      presetTop.addEventListener('click', () => {
        this.cameraController!.applyPreset('top');
      });
    }
    if (presetSide) {
      presetSide.addEventListener('click', () => {
        this.cameraController!.applyPreset('side');
      });
    }
    if (presetFront) {
      presetFront.addEventListener('click', () => {
        this.cameraController!.applyPreset('front');
      });
    }
    if (presetIsometric) {
      presetIsometric.addEventListener('click', () => {
        this.cameraController!.applyPreset('isometric');
      });
    }
    if (presetFollow) {
      presetFollow.addEventListener('click', () => {
        const playerFish = this.organismManager.getPlayerClownfish();
        if (playerFish) {
          this.cameraController!.applyPreset('follow', playerFish.getPosition());
        }
      });
    }

    // Camera mode toggle
    const toggleModeBtn = document.getElementById('toggle-camera-mode');
    if (toggleModeBtn) {
      toggleModeBtn.addEventListener('click', () => {
        const currentMode = this.cameraController!.getMode();
        if (currentMode === 'orbit') {
          this.cameraController!.setMode('freefly');
          toggleModeBtn.textContent = '🔄 Switch to Orbit';
          console.log('📷 FREE-FLY mode: WASD=Move, Shift+Mouse=Look, Space/Ctrl=Up/Down');
        } else {
          this.cameraController!.setMode('orbit');
          toggleModeBtn.textContent = '🔄 Switch to Free-Fly';
          console.log('📷 ORBIT mode: Drag=Rotate, WASD=Pan');
        }
      });
    }
  }

  private setupMatrixVisualizationToggle() {
    const toggleBtn = document.getElementById('toggle-matrix-viz');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        const isEnabled = this.environment.toggleMatrixVisualization();
        if (isEnabled) {
          toggleBtn.textContent = '✅ Hide 3D Matrix Field';
          toggleBtn.classList.add('active');
        } else {
          toggleBtn.textContent = '🔲 Show 3D Matrix Field';
          toggleBtn.classList.remove('active');
        }
      });
    }
  }

  private setupCreatureSelection() {
    const canvas = this.renderer.renderer.domElement;

    // Handle mouse clicks for selection
    canvas.addEventListener('click', (event) => {
      // Calculate mouse position in normalized device coordinates (-1 to +1)
      const rect = canvas.getBoundingClientRect();
      this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Update the picking ray with the camera and mouse position
      this.raycaster.setFromCamera(this.mouse, this.renderer.camera);

      // Calculate objects intersecting the picking ray
      const intersects = this.raycaster.intersectObjects(this.renderer.scene.children, true);

      if (intersects.length > 0) {
        // Find the first organism in the intersected objects
        for (const intersect of intersects) {
          let object: THREE.Object3D | null = intersect.object;

          // Traverse up to find organism
          while (object) {
            // Check if this object belongs to a hermit crab body group
            if (object.name === 'CrabBodyGroup' && object.parent) {
              this.selectCreature(object);
              return;
            }
            object = object.parent;
          }
        }
      }

      // If no organism was clicked, hide the profile panel
      this.deselectCreature();
    });

    // Close button for profile panel
    const closeBtn = document.getElementById('close-profile');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.deselectCreature();
      });
    }
  }

  private selectCreature(bodyGroup: THREE.Object3D) {
    // Find the organism that owns this body group
    const organisms = this.organismManager.getAllOrganisms();
    for (const organism of organisms) {
      if ('getBodyGroup' in organism) {
        const bodyGroupMethod = organism.getBodyGroup as () => THREE.Group;
        if (bodyGroupMethod.call(organism) === bodyGroup) {
          this.selectedOrganism = organism as any;
          this.updateCreatureProfile();
          const profilePanel = document.getElementById('creature-profile');
          if (profilePanel) {
            profilePanel.style.display = 'block';
          }
          console.log(`Selected: ${(organism as any).profile?.name || (organism as any).name}`);
          break;
        }
      }
    }
  }

  private deselectCreature() {
    this.selectedOrganism = null;
    const profilePanel = document.getElementById('creature-profile');
    if (profilePanel) {
      profilePanel.style.display = 'none';
    }
  }

  private updateCreatureProfile() {
    if (!this.selectedOrganism) return;

    const organism = this.selectedOrganism;
    const pos = organism.getPosition();

    // Update profile panel
    const nameEl = document.getElementById('creature-name');
    const speciesEl = document.getElementById('creature-species');
    const genderEl = document.getElementById('creature-gender');
    const positionEl = document.getElementById('creature-position');
    const personalityEl = document.getElementById('creature-personality');
    const moodEl = document.getElementById('creature-mood');
    const activityEl = document.getElementById('creature-activity');
    const healthEl = document.getElementById('creature-health');
    const hungerEl = document.getElementById('creature-hunger');
    const happinessEl = document.getElementById('creature-happiness');
    const shellQualityEl = document.getElementById('creature-shell-quality');
    const foodEl = document.getElementById('creature-food');

    const genderEmoji = ('getGender' in organism) ?
      (organism.getGender() === 'male' ? '♂️' : '♀️') : '';

    if (nameEl) nameEl.textContent = `${genderEmoji} ${organism.profile?.name || organism.name}`;
    if (speciesEl) speciesEl.textContent = organism.name || 'Unknown';

    if (genderEl && 'getGender' in organism) {
      const gender = organism.getGender();
      genderEl.textContent = gender === 'male' ? '♂️ Male' : '♀️ Female';
    }

    if (positionEl) positionEl.textContent = `X: ${pos.x.toFixed(1)}, Y: ${pos.y.toFixed(1)}, Z: ${pos.z.toFixed(1)}`;

    if (organism.profile) {
      if (personalityEl) personalityEl.textContent = organism.profile.personality || 'Unknown';
      if (moodEl) moodEl.textContent = organism.profile.mood || 'Unknown';
      if (foodEl) foodEl.textContent = organism.profile.favoriteFood || 'Unknown';
    }

    if (activityEl && 'getCurrentActivity' in organism) {
      activityEl.textContent = organism.getCurrentActivity();
    }

    if (healthEl && 'health' in organism) {
      healthEl.textContent = `${Math.round(organism.health)}%`;
    }

    if (hungerEl && 'getHunger' in organism) {
      hungerEl.textContent = `${Math.round(organism.getHunger())}%`;
    }

    if (happinessEl && 'getHappiness' in organism) {
      happinessEl.textContent = `${Math.round(organism.getHappiness())}%`;
    }

    if (shellQualityEl && 'getShellQuality' in organism) {
      shellQualityEl.textContent = `${Math.round(organism.getShellQuality())}%`;
    }
  }

  private updateTelemetry(deltaTime: number) {
    // Update FPS calculation
    this.frameCount++;
    const currentTime = performance.now();

    if (currentTime - this.fpsUpdateTime >= 1000) {
      this.currentFPS = this.frameCount;
      this.frameCount = 0;
      this.fpsUpdateTime = currentTime;
    }

    // Camera telemetry
    const camPosEl = document.getElementById('telem-camera');
    const camTargetEl = document.getElementById('telem-camera-target');

    if (camPosEl) {
      const pos = this.renderer.camera.position;
      camPosEl.textContent = `Pos: (${pos.x.toFixed(1)}, ${pos.y.toFixed(1)}, ${pos.z.toFixed(1)})`;
    }

    if (camTargetEl && this.cameraController) {
      const target = this.cameraController.getTarget();
      camTargetEl.textContent = `Target: (${target.x.toFixed(1)}, ${target.y.toFixed(1)}, ${target.z.toFixed(1)})`;
    }

    // Organism telemetry
    const organisms = this.organismManager.getAllOrganisms();
    const crabs = organisms.filter((org: any) => org.speciesId === 'pacific_hermit_crab');

    const organismsEl = document.getElementById('telem-organisms');
    const crabsEl = document.getElementById('telem-crabs');
    const sheldonEl = document.getElementById('telem-sheldon');
    const shelleyEl = document.getElementById('telem-shelley');

    if (organismsEl) organismsEl.textContent = `Total: ${organisms.length}`;
    if (crabsEl) crabsEl.textContent = `Hermit Crabs: ${crabs.length}`;

    // Find Sheldon and Shelley specifically
    const sheldon = crabs.find((c: any) => c.profile?.name === 'Sheldon');
    const shelley = crabs.find((c: any) => c.profile?.name === 'Shelley');

    if (sheldonEl) {
      if (sheldon) {
        const pos = sheldon.getPosition();
        sheldonEl.textContent = `Sheldon: (${pos.x.toFixed(1)}, ${pos.y.toFixed(1)}, ${pos.z.toFixed(1)})`;
        sheldonEl.style.color = '#0f0';
      } else {
        sheldonEl.textContent = 'Sheldon: NOT FOUND';
        sheldonEl.style.color = '#f00';
      }
    }

    if (shelleyEl) {
      if (shelley) {
        const pos = shelley.getPosition();
        shelleyEl.textContent = `Shelley: (${pos.x.toFixed(1)}, ${pos.y.toFixed(1)}, ${pos.z.toFixed(1)})`;
        shelleyEl.style.color = '#0f0';
      } else {
        shelleyEl.textContent = 'Shelley: NOT FOUND';
        shelleyEl.style.color = '#f00';
      }
    }

    // Rendering telemetry
    const sceneObjectsEl = document.getElementById('telem-scene-objects');
    const fpsEl = document.getElementById('telem-fps');
    const deltaEl = document.getElementById('telem-delta');

    if (sceneObjectsEl) {
      sceneObjectsEl.textContent = `Scene Objects: ${this.renderer.scene.children.length}`;
    }

    if (fpsEl) {
      const color = this.currentFPS < 30 ? '#f00' : this.currentFPS < 50 ? '#ff0' : '#0f0';
      fpsEl.innerHTML = `FPS: <span style="color: ${color}">${this.currentFPS}</span>`;
    }

    if (deltaEl) {
      deltaEl.textContent = `Delta: ${(deltaTime * 1000).toFixed(1)}ms`;
    }

    // Physics telemetry
    const physicsEl = document.getElementById('telem-physics');
    const timeEl = document.getElementById('telem-time');

    if (physicsEl) {
      // TODO: Get actual physics body count from PhysicsWorld if available
      physicsEl.textContent = `Bodies: ${organisms.length}`;
    }

    if (timeEl) {
      timeEl.textContent = `Sim Time: ${this.simulationTime.toFixed(2)}d`;
    }
  }

  private debugLog(message: string, color: string = '#fff') {
    const logEl = document.getElementById('telem-log');
    if (logEl) {
      const timestamp = new Date().toLocaleTimeString();
      const entry = `<div style="color: ${color}">[${timestamp}] ${message}</div>`;
      logEl.innerHTML = entry + logEl.innerHTML;

      // Keep only last 10 entries
      const entries = logEl.children;
      while (entries.length > 10) {
        logEl.removeChild(entries[entries.length - 1]);
      }
    }
  }

  togglePause(): boolean {
    this.isPaused = !this.isPaused;
    return this.isPaused;
  }

  adjustTimeScale(multiplier: number) {
    this.timeScale *= multiplier;
    this.timeScale = Math.max(0.1, Math.min(100, this.timeScale));
    console.log(`Time scale: ${this.timeScale.toFixed(2)}x`);
  }

  setTimeScale(scale: number) {
    this.timeScale = Math.max(0, Math.min(10, scale));
    console.log(`Time scale: ${this.timeScale.toFixed(2)}x`);
  }

  setMode(mode: SimulationMode) {
    this.mode = mode;
    this.environment.setMode(mode);

    if (mode === 'scientific') {
      this.timeScale = 1.0; // Real-time for scientific accuracy
    }

    console.log(`Mode: ${mode}`);
  }

  reset() {
    // Clear existing organisms
    this.organismManager.clear();

    // Reset environment
    this.environment.reset();

    // Reset time
    this.simulationTime = 0;
    this.timeScale = 1.0;

    // Respawn ecosystem
    this.spawnInitialEcosystem();

    console.log('✓ Simulation reset');
  }

  destroy() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.renderer.destroy();
    this.physicsWorld.destroy();
  }
}
